import { z } from "zod"

import { TenantIdSchema, UserIdSchema } from "./auth.js"
import { CorrectionRoundIdSchema, DocumentIdSchema } from "./id-schemas.js"

/**
 * CorrectionRound — first-class round of corrections on a Document.
 *
 * Real evidence: NC17 = 17 correction rounds on a single BL. Each round
 * captures who flagged, what changed, when it resolved, and the carrier
 * response. The four-layer correction-round automation (PRD §6b)
 * progressively reduces these — but the entity stays whether the round
 * is hand-flagged (L1), auto-diff-flagged (L2), risk-pattern-flagged
 * (L3), or carrier-API-amended (L4).
 */

const Bounded = (max = 256) => z.string().min(1).max(max)
const IsoDateTime = z.string().datetime()

export const CorrectionRoundStatusSchema = z.enum(["OPEN", "IN_PROGRESS", "RESOLVED", "REJECTED"])
export type CorrectionRoundStatus = z.infer<typeof CorrectionRoundStatusSchema>

export const CorrectionLayerSchema = z.enum([
	"L1_MANUAL",
	"L2_AUTO_DIFF",
	"L3_RISK_FLAG",
	"L4_CARRIER_API",
])
export type CorrectionLayer = z.infer<typeof CorrectionLayerSchema>

export const CorrectionFieldChangeSchema = z.object({
	fieldPath: Bounded(256),
	previousValue: z.string().max(4096).nullable(),
	newValue: z.string().max(4096).nullable(),
})
export type CorrectionFieldChange = z.infer<typeof CorrectionFieldChangeSchema>

export const CorrectionRoundSchema = z.object({
	id: CorrectionRoundIdSchema,
	tenantId: TenantIdSchema,
	documentId: DocumentIdSchema,
	roundNumber: z.number().int().positive(),
	layer: CorrectionLayerSchema,
	status: CorrectionRoundStatusSchema,
	requestedBy: UserIdSchema,
	requestedAt: IsoDateTime,
	changes: z.array(CorrectionFieldChangeSchema),
	resolvedBy: UserIdSchema.optional(),
	resolvedAt: IsoDateTime.optional(),
	carrierResponse: z.string().max(8192).optional(),
	notes: z.string().max(8192).optional(),
	createdAt: IsoDateTime,
	updatedAt: IsoDateTime,
})
export type CorrectionRound = z.infer<typeof CorrectionRoundSchema>
